import { html, nothing, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { cn } from "@/utilities/css";
import EkoElement from "@/utilities/element";
import style from "./radio-group.css?inline";
import "@/components/admin-icon";

@customElement("eko-radio-group")
export class RadioGroup extends EkoElement {
  static formAssociated = true;

  static shadowRootOptions = { ...EkoElement.shadowRootOptions, delegatesFocus: true };

  static styles = [...EkoElement.styles, unsafeCSS(style)];

  protected validationMessage: string = "Please select an option.";

  private focusFirstAvailableRadio = () => {
    const radios = Array.from(this.querySelectorAll("eko-radio")) as any[];
    if (radios.length === 0) return;

    // Focus the checked radio, or the first radio if none are checked
    const checkedRadio = radios.find((radio) => radio.value === this.value);
    const targetRadio = checkedRadio || radios[0];

    if (targetRadio) {
      targetRadio.focus();
    }
  };

  private handleChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    this.value = input.value;

    this.internals.setFormValue(this.value);
    this.checkValidity();

    // Update tabindex for roving focus
    this.updateTabindex();

    this.dispatchEvent(
      new CustomEvent("change", {
        bubbles: true,
        detail: { value: this.value },
      })
    );
  };

  private handleKeyDown = (event: any) => {
    const radios = Array.from(this.querySelectorAll("eko-radio")) as any[];
    const currentRadio = event.currentTarget as any;
    const currentIndex = radios.indexOf(currentRadio);

    if (currentIndex === -1) return;

    let targetIndex = -1;

    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        event.preventDefault();
        targetIndex = currentIndex === radios.length - 1 ? 0 : currentIndex + 1;
        break;
      case "ArrowUp":
      case "ArrowLeft":
        event.preventDefault();
        targetIndex = currentIndex === 0 ? radios.length - 1 : currentIndex - 1;
        break;
      default:
        return;
    }

    if (targetIndex >= 0) {
      const targetRadio = radios[targetIndex];

      // Update the value first
      this.value = targetRadio.value;

      // Update the checked state
      targetRadio.checked = true;

      // Update tabindex for roving focus immediately
      this.updateTabindex();

      // Focus the target radio
      targetRadio.focus();

      // Update form value and validation
      this.internals.setFormValue(this.value);
      this.checkValidity();

      // Dispatch change event
      this.dispatchEvent(
        new CustomEvent("change", {
          bubbles: true,
          detail: { value: this.value },
        })
      );
    }
  };

  private handleSlotChange = () => {
    // Group the radios and add event listeners
    const radios = this.querySelectorAll("eko-radio");
    radios.forEach((radio: any) => {
      radio.name = this.name;

      radio.removeEventListener("change", this.handleChange);
      radio.removeEventListener("keydown", this.handleKeyDown);
      radio.addEventListener("change", this.handleChange);
      radio.addEventListener("keydown", this.handleKeyDown);

      // Set initial state
      if (radio.value === this.value) {
        radio.checked = true;
      }
    });

    this.updateTabindex();

    // Set initial form value and validate
    this.internals.setFormValue(this.value || null);
    this.checkValidity();
  };

  private internals: ElementInternals;

  private updateTabindex = () => {
    const radios = this.querySelectorAll("eko-radio");
    radios.forEach((radio: any, index: number) => {
      const isFirstRadio = index === 0;
      const isCheckedRadio = radio.value === this.value;
      const shouldBeTabbable = isCheckedRadio || (!this.value && isFirstRadio);
      radio.tabIndex = shouldBeTabbable ? 0 : -1;
    });
  };

  @property({ reflect: true, type: Boolean }) admin: boolean = false;
  @property() class: string = "";
  @property() description: string = "";
  @property({ reflect: true }) direction: "vertical" | "horizontal" = "vertical";
  @property({ reflect: true, type: Boolean }) disabled: boolean = false;
  @state() hasError: boolean = false;
  @property() label: string = "";
  @property({ reflect: true }) name: string = "";
  @property({ reflect: true, type: Boolean }) required: boolean = false;
  @property({ type: String }) value: string = "";

  constructor() {
    super();
    this.internals = this.attachInternals();
  }

  checkValidity() {
    if (this.required && !this.value) {
      this.internals.setValidity({ valueMissing: true }, this.validationMessage);
      return false;
    }

    this.internals.setValidity({});
    return this.internals.checkValidity();
  }

  focus() {
    this.focusFirstAvailableRadio();
  }

  formAssociatedCallback() {}

  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled;
  }

  formResetCallback() {
    this.value = "";
    this.internals.setFormValue(null);
    this.internals.setValidity({});
  }

  formStateRestoreCallback(state: string | null) {
    if (state) {
      this.value = state;
      this.internals.setFormValue(state);
    } else {
      this.value = "";
      this.internals.setFormValue(null);
    }
  }

  render() {
    const hasValidationError = this.internals?.validity?.valid === false;

    return html`
      <fieldset
        class=${cn("eko-radio-group", this.class)}
        role="radiogroup"
        aria-required=${this.required ? "true" : "false"}
        aria-invalid=${hasValidationError ? "true" : "false"}
        tabindex=${hasValidationError ? "0" : "-1"}
        @focus=${this.focusFirstAvailableRadio}
        ?disabled=${this.disabled}
        part="root">
        ${
          this.label || this.description
            ? html`<div part="header">
                ${this.label
                  ? html`<legend class="eko-radio-group__label" part="label">
                      ${this.label}
                      ${this.required
                        ? html`<span class="eko-radio-group__required" part="required">*</span>`
                        : nothing}
                      ${this.admin
                        ? html`<span class="eko-radio-group__admin"
                            ><eko-admin-icon tooltip></eko-admin-icon
                          ></span>`
                        : nothing}
                    </legend>`
                  : nothing}
                ${this.description
                  ? html`<p class="eko-radio-group__description" part="description">
                      ${this.description}
                    </p>`
                  : nothing}
              </div>`
            : nothing
        }
          <div class="eko-radio-group__wrapper" part="wrapper">
            <slot @slotchange=${this.handleSlotChange}></slot>
          </div>
        </fieldset>
      </fieldset>
    `;
  }

  reportValidity() {
    return this.internals.reportValidity();
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has("value")) {
      // Update radio states when value changes
      const radios = this.querySelectorAll("eko-radio");
      radios.forEach((radio: any) => {
        radio.checked = radio.value === this.value;
      });

      // Update tabindex for roving focus
      this.updateTabindex();

      // Update form value and check validity
      this.internals.setFormValue(this.value || null);
      this.checkValidity();
    }

    if (changedProperties.has("disabled")) {
      this.internals.ariaDisabled = this.disabled ? "true" : "false";

      // Update radio disabled states
      const radios = this.querySelectorAll("eko-radio");
      radios.forEach((radio: any) => {
        radio.disabled = this.disabled;
      });
    }

    if (changedProperties.has("required")) {
      this.checkValidity();
    }
  }

  get form() {
    return this.internals.form;
  }

  get type() {
    return this.localName;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-radio-group": HTMLInputElement;
  }
}
